import React from 'react';
import { StyleSheet, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import Popover from 'react-native-popover-view';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setAutomaticChoice } from '../../../redux/slices/newSellscreenSlice';
import { PricedLine } from '../../../redux/utils/discountEngine';
import { formatMinor } from './discountLabels';

// Lets the cashier switch a line between matching automatic discounts, or turn them off
const AutomaticDiscountPicker = ({ visible, onClose, line }: { visible: boolean, onClose: () => void, line: PricedLine }) => {
	const dispatch = useAppDispatch()
	const { width } = useWindowDimensions()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const selectedId = line.discount?.kind === "AUTOMATIC" ? line.discount.discountId : null

	const choose = (discountId: number | "NONE") => {
		dispatch(setAutomaticChoice({ locationId, variantId: line.variantId, discountId }))
		onClose()
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				const Option = ({ id, title, amount }: { id: number | "NONE", title: string, amount?: number }) => {
					const isSelected = id === "NONE" ? selectedId == null : selectedId === id
					return (
						<TouchableOpacity style={[styles.option, isSelected && styles.optionSelected]} onPress={() => choose(id)}>
							<ZenIcon type='material-community' name={isSelected ? 'radiobox-marked' : 'radiobox-blank'} size={18} color={isSelected ? theme?.primary : theme?.grey} />
							<ZenText content={title} style={styles.optionTitle} />
							{amount != null ? <ZenText content={`-${formatMinor(currencySymbol, amount)}`} style={styles.optionAmount} /> : null}
						</TouchableOpacity>
					)
				}
				return (
					<Popover isVisible={visible} onRequestClose={onClose} popoverStyle={{ width: width > 900 ? 460 : width > 600 ? 420 : 330, borderRadius: 12 }}>
						<View style={styles.container}>
							<View style={styles.header}>
								<ZenText content={`Discount for ${line.name}`} style={styles.title} />
								<TouchableOpacity onPress={onClose}>
									<ZenIcon name='close' type='antdesign' size={18} color={theme?.disabledGray500} />
								</TouchableOpacity>
							</View>
							<ZenText content="Only one automatic discount applies per item. Amounts are for this line." style={styles.hint} />
							{line.automaticOptions.map((option) => (
								<Option key={option.discountId} id={option.discountId} title={option.title} amount={option.amount} />
							))}
							<Option id="NONE" title="No automatic discount" />
						</View>
					</Popover>
				)
			}}
		</ThemeConsumer>
	)
}

const style = (theme: Theme) => StyleSheet.create({
	container: { padding: 16, gap: 8 },
	header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
	title: { fontSize: theme?.smallFontSize, fontFamily: theme?.bold, color: theme?.lightDark, flex: 1 },
	hint: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.grey, marginBottom: 4 },
	option: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10, borderWidth: 1, borderColor: theme?.coolGray },
	optionSelected: { borderColor: theme?.primary, backgroundColor: theme?.lightPrimary },
	optionTitle: { flex: 1, fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.lightDark },
	optionAmount: { fontSize: theme?.errorFontSize, fontFamily: theme?.bold, color: theme?.tealGreen },
})

export default AutomaticDiscountPicker
